import React, { useState, useEffect } from 'react';
import { Globe } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const pages = [
  { key: 'home', label: 'Anasayfa' },
  { key: 'corporate', label: 'Kurumsal' },
  { key: 'projects', label: 'Projeler' },
  { key: 'catalog', label: 'E-Katalog' },
  { key: 'contact', label: 'İletişim' }
];

export default function SEOSettings() {
  const [seoData, setSeoData] = useState({});
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState('home');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    api.get('/seo').then(r => {
      const data = {};
      r.data.forEach(item => { data[item.page] = item; });
      setSeoData(data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setIsSubmitting(true);
    const current = seoData[activePage] || {};
    try {
      await api.put(`/seo/${activePage}`, {
        title: current.title || '',
        description: current.description || '',
        keywords: current.keywords || '',
        og_image: current.og_image || ''
      });
      toast.success('SEO ayarları kaydedildi');
    } catch (err) {
      toast.error('Hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateField = (field, value) => {
    setSeoData(prev => ({
      ...prev,
      [activePage]: { ...prev[activePage], [field]: value }
    }));
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>;

  const current = seoData[activePage] || {};

  return (
    <div data-testid="admin-seo-settings">
      <h1 className="font-heading text-2xl font-semibold mb-6">SEO Ayarları</h1>

      {/* Page Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {pages.map(p => (
          <button
            key={p.key}
            onClick={() => setActivePage(p.key)}
            className={`px-4 py-2 text-sm rounded-lg transition-colors ${
              activePage === p.key ? 'bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))]' : 'bg-secondary hover:bg-secondary/80'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Form */}
      <div className="space-y-6">
        <div className="p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">{pages.find(p => p.key === activePage)?.label} SEO</h3>
          <div className="space-y-4">
            <Field label="Sayfa Başlığı (Title)" value={current.title || ''} onChange={v => updateField('title', v)} />
            <div>
              <label className="block text-sm font-medium mb-1.5">Meta Açıklama (Description)</label>
              <textarea value={current.description || ''} onChange={e => updateField('description', e.target.value)} rows={3} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))] resize-none" />
            </div>
            <Field label="Anahtar Kelimeler" value={current.keywords || ''} onChange={v => updateField('keywords', v)} placeholder="insaat, proje, konut (virgül ile ayırın)" />
            <Field label="OG Image URL" value={current.og_image || ''} onChange={v => updateField('og_image', v)} placeholder="https://..." />
          </div>
        </div>

        {/* SEO Preview */}
        <div className="p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-[hsl(var(--gold))]" /> Google Önizleme
          </h3>
          <div className="p-4 rounded-xl bg-white border border-gray-200">
            <div className="text-blue-700 text-lg font-medium truncate">{current.title || 'Sayfa Başlığı'}</div>
            <div className="text-green-700 text-sm truncate">www.sirketadi.com/{activePage === 'home' ? '' : activePage}</div>
            <div className="text-gray-600 text-sm mt-1 line-clamp-2">{current.description || 'Sayfa aciklamasi burada görünecek...'}</div>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={isSubmitting}
          className="px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] disabled:opacity-50"
          data-testid="admin-save-button"
        >
          {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="block text-sm font-medium mb-1.5">{label}</label>
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]" />
  </div>
);
